import { fragments } from "Apollo/Queries";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import { gql, useMutation } from "@apollo/client";

const MY_FAVORITES = gql`
  query getFavorites($limit: Int, $page: Int) {
    favorites(pagination: { limit: $limit, page: $page }) {
      pagination {
        limit
        page
        pages
        count
      }
      items {
        id
        item {
          ...ProductOverviewFragment
        }
      }
    }
  }
  ${fragments.productOverview}
`;

const CREATE_FAVORITE = gql`
  mutation CreateFavorite($object_id: UUID!) {
    createFavorite(input: { object_id: $object_id, content_type: PRODUCT }) {
      id
      item {
        ... on Product {
          id
          title
        }
      }
    }
  }
`;

const REMOVE_FAVORITE = gql`
  mutation RemoveFavorite($id: UUID!) {
    removeFavorite(id: $id)
  }
`;

const useToggleFavorite = item => {
  const [create] = useMutation(CREATE_FAVORITE, {
    refetchQueries: [{ query: MY_FAVORITES }],
    update: (cache, { data }) => {
      cache.modify({
        id: cache.identify(item),
        fields: {
          favorite() {
            return { id: data.createFavorite.id };
          },
        },
      });
    },
  });
  const [remove] = useMutation(REMOVE_FAVORITE, {
    optimisticResponse: {
      removeFavorite: true,
    },
    update: cache => {
      cache.evict({
        id: cache.identify(item),
        fieldName: "favorite",
      });
      cache.modify({
        fields: {
          favorites(existing, { readField }) {
            const { limit, count, page } = existing?.pagination || {};
            return {
              items: existing?.items?.filter(
                favoriteRef =>
                  item?.favorite?.id !== readField("id", favoriteRef)
              ),
              pagination: {
                limit,
                count: count - 1,
                page: Math.min(page, Math.ceil((count - 1) / limit)),
                pages: Math.ceil((count - 1) / limit),
              },
            };
          },
        },
      });
    },
  });

  const favorite = item?.favorite;

  /** WHO-6629: resolves "added" or "removed", so a Pro Buyer's heart can open the project menu after a save. */
  const toggleFavorite = async () => {
    if (favorite) {
      await remove({
        variables: {
          id: favorite.id,
        },
      });

      return "removed";
    }

    await create({
      variables: {
        object_id: item.id,
      },
    });

    analyticsService.trackAddToWishlist(item);

    return "added";
  };

  return {
    toggleFavorite,
    isFavorite: !!favorite,
  };
};

export default useToggleFavorite;
